import React, { useEffect, useMemo, useState } from "react";
import { AiOutlineFolder, AiOutlineTag, AiOutlineRight, AiOutlineDown, AiOutlinePlus, AiOutlineEdit, AiOutlineDelete } from "react-icons/ai";
import { useArbolDocumentalService } from "../../services/api";

const DEFAULT_STORAGE_KEY = "boveda.doctree.expanded";
// Mantengo el nombre exportado por backward-compat con DocTreeSidebar.test.jsx
const EXPANDED_STORAGE_KEY = DEFAULT_STORAGE_KEY;

const loadExpanded = (storageKey) => {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

const saveExpanded = (storageKey, state) => {
  try {
    localStorage.setItem(storageKey, JSON.stringify(state));
  } catch {
    // ignore storage failures (quota, private mode, etc.)
  }
};

// Convierte la lista anidada {id,name,isSeries,folders} al shape interno
// {id, name, isSeries, children, _raw} usado por el renderer recursivo.
// Series no tienen `children`. `_raw` preserva el nodo original (con _data u
// otros campos) para que renderSeriePills lo pueda leer.
const toArboristData = (folders) =>
  (folders || []).map((f) => {
    const node = { id: String(f.id), name: f.name, isSeries: !!f.isSeries, _raw: f };
    if (!f.isSeries) {
      node.children = toArboristData(f.folders || []);
    }
    return node;
  });

// Busca un nodo por id en la estructura original y devuelve el path desde raíz.
const findPath = (folders, targetId) => {
  for (const f of folders || []) {
    if (String(f.id) === String(targetId)) return [f];
    if (!f.isSeries) {
      const sub = findPath(f.folders || [], targetId);
      if (sub) return [f, ...sub];
    }
  }
  return null;
};

// Construye un árbol anidado {id,name,isSeries,folders,_data} a partir de
// dos listas planas: folders ({id,name,parent}) y series ({id,name,parent|padre,...}).
// `_data` queda en cada nodo con el objeto original (útil para que
// renderSeriePills lea flags, badges, etc.).
//
// Acepta tanto el campo en inglés (`parent`) como el legacy castellano (`padre`)
// para ser resiliente al normalizer de cada caller (post #57 y rework #19 las
// series ya vienen con `parent`; folders también).
const buildTreeFromFlat = (folders, series) => {
  const byId = new Map();
  const getParent = (n) => {
    const raw = n.parent != null ? n.parent : n.padre;
    return raw != null && raw !== '' ? String(raw) : null;
  };

  (folders || []).forEach((f) => {
    byId.set(String(f.id), {
      id: String(f.id),
      name: f.name,
      isSeries: false,
      folders: [],
      _parentId: getParent(f),
      _data: f,
    });
  });

  (series || []).forEach((s) => {
    byId.set(String(s.id), {
      id: String(s.id),
      name: s.name,
      isSeries: true,
      _parentId: getParent(s),
      _data: s,
    });
  });

  const roots = [];
  byId.forEach((node) => {
    const parentId = node._parentId;
    if (parentId && byId.has(parentId)) {
      const parent = byId.get(parentId);
      if (parent.isSeries) {
        // Defensivo: las series no pueden tener hijos. Tratamos como raíz.
        roots.push(node);
      } else {
        parent.folders.push(node);
      }
    } else {
      roots.push(node);
    }
  });

  const getOrder = (n) => {
    if (!n._data) return null;
    const o = n._data.order != null ? n._data.order : n._data.orden;
    return Number.isFinite(o) ? o : null;
  };
  const sortChildren = (nodes) => {
    nodes.sort((a, b) => {
      const oa = getOrder(a);
      const ob = getOrder(b);
      if (oa != null && ob != null && oa !== ob) return oa - ob;
      return (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" });
    });
    nodes.forEach((n) => { if (!n.isSeries) sortChildren(n.folders); });
  };
  sortChildren(roots);

  byId.forEach((node) => { delete node._parentId; });
  return roots;
};

const DefaultPills = ({ counts, isLoading }) => {
  if (isLoading) {
    return <span style={styles.pillLoading}>...</span>;
  }
  if (!counts) return null;
  if (counts.accessible === 0) {
    return (
      <span style={styles.pillNoAccess} title="Sin acceso a documentos de esta serie">
        0
      </span>
    );
  }
  if (counts.accessible === counts.total) {
    return (
      <span style={styles.pillAccessible} title={`${counts.accessible} documentos accesibles`}>
        {counts.accessible}
      </span>
    );
  }
  return (
    <>
      <span style={styles.pillTotal} title={`Total: ${counts.total} publicados`}>
        {counts.total}
      </span>
      <span style={styles.pillAccessible} title={`Accesibles: ${counts.accessible} de ${counts.total}`}>
        {counts.accessible}
      </span>
    </>
  );
};

// Renderer recursivo no-virtualizado: cada fila auto-ajusta su altura según
// contenido (folders sin pills quedan compactos; series con pills se expanden
// solo lo necesario). Trade-off: sin virtualización — adecuado para el volumen
// de Bóveda (decenas / cientos de nodos), no millones.
const TreeNode = ({
  node,
  level,
  expanded,
  selectedId,
  serieCounts,
  loadingIds,
  renderSeriePills,
  pillsPosition,
  onToggle,
  onActivate,
  folderActions,
}) => {
  const isSeries = !!node.isSeries;
  const hasChildren = !isSeries && Array.isArray(node.children) && node.children.length > 0;
  // Semántica: `expanded[id] === false` → user lo colapsó explícitamente.
  // Cualquier otro valor (undefined o true) → abierto por default. Coincide
  // con el comportamiento original de react-arborist (`openByDefault ?? true`).
  const isOpen = expanded[node.id] !== false;
  const isSelected = selectedId === node.id;
  const counts = serieCounts[node.id];
  const isLoading = loadingIds.has(node.id);

  let pillsContent = null;
  if (isSeries) {
    if (renderSeriePills) {
      pillsContent = renderSeriePills(node.id, node._raw || node);
    } else if (counts || isLoading) {
      pillsContent = <DefaultPills counts={counts} isLoading={isLoading} />;
    }
  }
  const stackBelow = pillsPosition === "below" && pillsContent;

  const handleClick = (e) => {
    e.stopPropagation();
    if (!isSeries && hasChildren) {
      onToggle(node.id);
    }
    onActivate(node);
  };

  return (
    <div>
      <div
        onClick={handleClick}
        data-testid={`doctree-node-${node.id}`}
        data-node-id={node.id}
        data-is-series={isSeries ? "true" : "false"}
        style={{
          ...styles.nodeRow,
          ...(stackBelow ? styles.nodeRowStacked : {}),
          ...(isSelected ? styles.nodeRowSelected : {}),
          paddingLeft: `${level * 20}px`,
        }}
      >
        <span style={styles.chevron}>
          {hasChildren ? (
            isOpen ? (
              <AiOutlineDown style={styles.chevronIcon} data-testid={`chevron-open-${node.id}`} />
            ) : (
              <AiOutlineRight style={styles.chevronIcon} data-testid={`chevron-closed-${node.id}`} />
            )
          ) : null}
        </span>
        <span style={styles.typeIcon}>
          {isSeries ? <AiOutlineTag /> : <AiOutlineFolder />}
        </span>
        {stackBelow ? (
          <span style={styles.nameWithPillsBelow}>
            <span style={styles.nodeName} title={node.name}>
              {node.name}
            </span>
            <span style={styles.pillsContainerBelow}>{pillsContent}</span>
          </span>
        ) : (
          <>
            <span style={styles.nodeName} title={node.name}>
              {node.name}
            </span>
            {pillsContent && (
              <span style={styles.pillsContainer}>{pillsContent}</span>
            )}
          </>
        )}
        {/* Toolbar admin: solo en folders, solo si el caller pasó folderActions.
            Aparece on-hover (CSS abajo). */}
        {!isSeries && folderActions && (
          <span className="doctree-folder-actions" style={styles.folderActions}>
            {folderActions.onCreate && (
              <button
                type="button"
                title="Crear subfolder"
                aria-label="Crear subfolder"
                onClick={(e) => { e.stopPropagation(); folderActions.onCreate(node); }}
                style={styles.folderActionBtn}
                data-testid={`folder-action-create-${node.id}`}
              >
                <AiOutlinePlus />
              </button>
            )}
            {folderActions.onEdit && (
              <button
                type="button"
                title="Renombrar / mover folder"
                aria-label="Renombrar folder"
                onClick={(e) => { e.stopPropagation(); folderActions.onEdit(node); }}
                style={styles.folderActionBtn}
                data-testid={`folder-action-edit-${node.id}`}
              >
                <AiOutlineEdit />
              </button>
            )}
            {folderActions.onDelete && (
              <button
                type="button"
                title="Eliminar folder"
                aria-label="Eliminar folder"
                onClick={(e) => { e.stopPropagation(); folderActions.onDelete(node); }}
                style={{ ...styles.folderActionBtn, color: '#B91C1C' }}
                data-testid={`folder-action-delete-${node.id}`}
              >
                <AiOutlineDelete />
              </button>
            )}
          </span>
        )}
      </div>
      {hasChildren && isOpen && (
        <div>
          {node.children.map((child) => (
            <TreeNode
              key={child.id}
              node={child}
              level={level + 1}
              expanded={expanded}
              selectedId={selectedId}
              serieCounts={serieCounts}
              loadingIds={loadingIds}
              renderSeriePills={renderSeriePills}
              pillsPosition={pillsPosition}
              onToggle={onToggle}
              onActivate={onActivate}
              folderActions={folderActions}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const DocTreeSidebar = ({
  folders,
  selectedPath,
  onFolderClick,
  title = "Árbol Documental",
  storageKey = DEFAULT_STORAGE_KEY,
  renderSeriePills,
  noFetchCounts = false,
  emptyMessage = "No hay elementos en este nivel",
  containerStyle,
  pillsPosition = "inline", // "inline" (default) o "below" (apila las pills debajo del nombre)
  // rowHeight queda como prop ignorado por backward-compat con callers post-refactor
  // pre-recursivo. El renderer recursivo auto-ajusta cada fila al contenido.
  // eslint-disable-next-line no-unused-vars
  rowHeight,
  folderActions, // { onCreate?, onEdit?, onDelete? } — habilita toolbar admin
}) => {
  const arbolService = useArbolDocumentalService();
  const [serieCounts, setSerieCounts] = useState({});
  const [loadingIds, setLoadingIds] = useState(new Set());
  const [expanded, setExpanded] = useState(() => loadExpanded(storageKey));

  const data = useMemo(() => toArboristData(folders), [folders]);

  // Collect all series ids reachable in the current tree to prefetch counts.
  const allSerieIds = useMemo(() => {
    const acc = [];
    const walk = (nodes) => {
      for (const n of nodes || []) {
        if (n.isSeries) acc.push(n.id);
        else walk(n.folders || []);
      }
    };
    walk(folders || []);
    return acc;
  }, [folders]);

  useEffect(() => {
    if (noFetchCounts) return;
    allSerieIds.forEach((id) => {
      if (serieCounts[id] || loadingIds.has(id)) return;
      setLoadingIds((prev) => new Set(prev).add(id));
      arbolService
        .getSerieCounts(id)
        .then((response) => {
          setSerieCounts((prev) => ({
            ...prev,
            [id]: {
              total: response.data.publishedDocCount,
              accessible: response.data.accessiblePublishedDocCount,
            },
          }));
        })
        .catch((err) => {
          console.error(`Error loading counts for ${id}:`, err);
          setSerieCounts((prev) => ({ ...prev, [id]: { total: 0, accessible: 0 } }));
        })
        .finally(() => {
          setLoadingIds((prev) => {
            const next = new Set(prev);
            next.delete(id);
            return next;
          });
        });
    });
  }, [allSerieIds, arbolService, serieCounts, loadingIds, noFetchCounts]);

  // Expand path to selected node automatically.
  useEffect(() => {
    if (!selectedPath || selectedPath.length === 0) return;
    setExpanded((prev) => {
      const next = { ...prev };
      // Open every ancestor folder along the selected path (excluding the leaf series).
      for (let i = 0; i < selectedPath.length - 1; i++) {
        const id = String(selectedPath[i].id);
        next[id] = true;
      }
      saveExpanded(storageKey, next);
      return next;
    });
  }, [selectedPath, storageKey]);

  const selectedId = selectedPath && selectedPath.length > 0
    ? String(selectedPath[selectedPath.length - 1].id)
    : null;

  const handleActivate = (node) => {
    const path = findPath(folders, node.id);
    if (path) onFolderClick(path);
  };

  const handleToggle = (id) => {
    setExpanded((prev) => {
      const next = { ...prev };
      // Toggle entre "user lo colapsó" (false) y "default abierto" (sin entry).
      if (next[id] === false) delete next[id];
      else next[id] = false;
      saveExpanded(storageKey, next);
      return next;
    });
  };

  return (
    <aside style={{ ...styles.container, ...(containerStyle || {}) }} data-testid="doctree-sidebar">
      {/* Hover visibility para la toolbar de folder admin (no se puede hacer con
          inline styles porque el inline gana en especificidad). Scoped por className. */}
      <style>{`
        .doctree-folder-actions { opacity: 0; transition: opacity 0.15s ease; }
        [data-testid^="doctree-node-"]:hover .doctree-folder-actions { opacity: 1; }
        [data-testid^="doctree-node-"]:focus-within .doctree-folder-actions { opacity: 1; }
        .doctree-folder-actions button:hover { background-color: rgba(0,70,139,0.08); }
      `}</style>
      {title ? <h3 style={styles.title}>{title}</h3> : null}
      <div style={styles.treeContainer}>
        {data.length === 0 ? (
          <p style={styles.emptyMessage}>{emptyMessage}</p>
        ) : (
          <div data-testid="doctree-root">
            {data.map((node) => (
              <TreeNode
                key={node.id}
                node={node}
                level={0}
                expanded={expanded}
                selectedId={selectedId}
                serieCounts={serieCounts}
                loadingIds={loadingIds}
                renderSeriePills={renderSeriePills}
                pillsPosition={pillsPosition}
                onToggle={handleToggle}
                onActivate={handleActivate}
                folderActions={folderActions}
              />
            ))}
          </div>
        )}
      </div>
    </aside>
  );
};

const styles = {
  container: {
    width: "280px",
    borderRight: "1px solid #ddd",
    padding: "20px 10px 20px 20px",
    backgroundColor: "#F9F9F9",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  title: {
    fontSize: "18px",
    fontWeight: "bold",
    color: "#00468B",
    marginBottom: "10px",
    flexShrink: 0,
  },
  treeContainer: {
    flex: 1,
    minHeight: 0,
    width: "100%",
    overflow: "auto",
  },
  emptyMessage: {
    padding: "10px",
    color: "#888",
    fontSize: "14px",
  },
  nodeRow: {
    display: "flex",
    alignItems: "center",
    cursor: "pointer",
    paddingTop: "4px",
    paddingBottom: "4px",
    paddingRight: "6px",
    borderRadius: "4px",
    color: "#00468B",
    fontSize: "14px",
    userSelect: "none",
  },
  nodeRowStacked: {
    // En modo "pills below", el icono queda alineado arriba para que el nombre
    // y la fila de pills mantengan su layout vertical limpio.
    alignItems: "flex-start",
    paddingTop: "4px",
    paddingBottom: "4px",
  },
  nameWithPillsBelow: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    minWidth: 0,
    gap: "2px",
  },
  nodeRowSelected: {
    backgroundColor: "#E3F2FD",
    fontWeight: "600",
  },
  chevron: {
    width: "14px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  chevronIcon: {
    fontSize: "10px",
    color: "#666",
  },
  typeIcon: {
    marginRight: "6px",
    marginLeft: "4px",
    display: "inline-flex",
    alignItems: "center",
    fontSize: "16px",
    flexShrink: 0,
  },
  nodeName: {
    flex: 1,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  pillsContainer: {
    display: "inline-flex",
    gap: "4px",
    marginLeft: "8px",
    flexShrink: 0,
    alignItems: "center",
  },
  pillsContainerBelow: {
    display: "flex",
    flexWrap: "wrap",
    gap: "4px",
    alignItems: "center",
  },
  pillTotal: {
    backgroundColor: "#E3F2FD",
    color: "#1976D2",
    padding: "2px 8px",
    borderRadius: "12px",
    fontSize: "11px",
    fontWeight: "600",
    whiteSpace: "nowrap",
  },
  pillAccessible: {
    backgroundColor: "#E8F5E9",
    color: "#388E3C",
    padding: "2px 8px",
    borderRadius: "12px",
    fontSize: "11px",
    fontWeight: "600",
    whiteSpace: "nowrap",
  },
  pillNoAccess: {
    backgroundColor: "#F5F5F5",
    color: "#757575",
    padding: "2px 8px",
    borderRadius: "12px",
    fontSize: "11px",
    fontWeight: "600",
    whiteSpace: "nowrap",
  },
  pillLoading: {
    backgroundColor: "#F5F5F5",
    color: "#9E9E9E",
    padding: "2px 8px",
    borderRadius: "12px",
    fontSize: "11px",
    whiteSpace: "nowrap",
  },
  folderActions: {
    // opacity y transition se manejan via className (.doctree-folder-actions)
    // porque el inline style gana en especificidad y bloquearía el :hover.
    display: "inline-flex",
    gap: "2px",
    marginLeft: "6px",
    flexShrink: 0,
  },
  folderActionBtn: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "22px",
    height: "22px",
    border: "none",
    background: "transparent",
    cursor: "pointer",
    borderRadius: "4px",
    color: "#00468B",
    fontSize: "13px",
    padding: 0,
  },
};

export default DocTreeSidebar;
export { findPath, toArboristData, buildTreeFromFlat, EXPANDED_STORAGE_KEY };
